"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { useSlotRegistry } from "@/components/article-shell/ArticleShellContext";

/**
 * 요약 PDF 상품 상세 랜딩(JCG-2121)의 셸 슬롯 주입 래퍼.
 *
 * 데스크탑 custom 셸(ArticleShell variant="custom") 안에서는:
 * - 좌측 슬롯(customLeftSlot) ← `leftAside`(Complete 이용권 안내 배너 + 적중 실적 배너 등 컴팩트 사이드 배너)
 * - 우측 슬롯(customRightSlot) ← sticky 구매 CTA(`cta` = ProductInfo 결제 박스)
 * - 가운데(children) ← 세일즈 본문(캐러셀 + 상세 설명)
 *
 * JCG-2121 후속: 좌측 슬롯은 섹션 앵커 scroll-spy 네비(PdfDetailSectionNav)에서 컴팩트
 * 사이드 배너(`leftAside`)로 교체됐다. 본문의 "Complete 이용권 안내"·"적중 실적" 블록은
 * 셸에선 좌측 사이드바로 이동(중복 방지), 모바일 폴백(fallback)에선 본문에 그대로 유지한다
 * (폴백엔 좌측 사이드바가 없음).
 *
 * 셸 밖(모바일/태블릿/no-JS/SSR 첫 렌더)에서는 기존 클래식 풀블리드 랜딩으로 폴백한다 —
 * `fallback` 노드를 그대로 렌더(레이아웃·결제 박스 위치 byte-identical). 각 상세 클라이언트가
 * 폴백 마크업을 들고 있고, 이 래퍼는 셸일 때만 슬롯으로 재배치한다.
 *
 * ## 결제(Nicepay) 보존
 * `cta`(=ProductInfo) element 는 호출부가 한 번 만들어 넘기고, 이 래퍼는 셸이면 우측 슬롯에
 * portal, 폴백이면 호출부의 fallback 안에서 인라인 렌더한다 — 결제 핸들러/폴링/모달/콜백/GSI
 * 로직은 ProductInfo 내부에 그대로 있어 *이동만, 변경 없음*.
 *
 * ## SSR-safe 폴백 계약
 * 폴백 판정은 `inShell` 단독으로만 한다(`if (!inShell) return fallback`). 셸 안이면 중앙
 * 본문(children)을 항상 렌더하고, 좌/우 슬롯은 각각 독립 가드(`slot && createPortal(...)`)로
 * 채워질 때만 portal 한다. 슬롯이 일시적으로 null(좌측 사이드바 접기, 슬롯 ref 채워지기 전
 * 첫 프레임)이어도 전체 출력을 폴백으로 무너뜨리지 않는다 — 우측 결제 portal 이 함께
 * 언마운트되어 결제 폴링/GSI 인증 상태가 끊기는 회귀를 막기 위함(ForumJcgIndex/HomeDashboard
 * custom 셸의 per-slot 독립 가드 계약과 동일).
 */
interface PdfDetailShellProps {
  /**
   * 좌측 사이드 배너(셸일 때만 좌측 슬롯에 portal) — "Complete 이용권 안내" 배너 +
   * (상품에 있을 때) "적중 실적" 배너 등. 없으면(null) 좌측 슬롯을 비운다(접힘과 동일).
   * 좁은 좌측 컬럼(~320–560px)에 맞춘 컴팩트 세로 스택을 전달할 것.
   */
  leftAside?: React.ReactNode;
  /** 가운데 본문(셸일 때만) — 캐러셀 + 세일즈 상세. */
  children: React.ReactNode;
  /** 우측 sticky 구매 CTA(ProductInfo 결제 박스). 셸일 때만 우측 슬롯에 portal. */
  cta: React.ReactNode;
  /** 셸 밖(모바일/태블릿/SSR) 폴백 — 기존 클래식 풀블리드 랜딩 전체. */
  fallback: React.ReactNode;
}

export default function PdfDetailShell({
  leftAside,
  children,
  cta,
  fallback,
}: PdfDetailShellProps) {
  const { inShell, left, right } = useSlotRegistry();
  const leftSlot = left.customLeftSlot;
  const rightSlot = right.customRightSlot;

  // 셸 밖(모바일/태블릿/no-JS/SSR 첫 렌더)에서는 클래식 폴백을 그대로 렌더.
  // 폴백 판정은 `inShell` 단독으로만 한다 — 슬롯이 일시적으로 null(좌측 사이드바 접기,
  // 슬롯 ref 채워지기 전 첫 프레임)이라고 해서 전체 출력을 폴백으로 무너뜨리면,
  // 우측 결제 portal(ProductInfo/PaymentSection)이 언마운트→리마운트되며 결제 폴링/
  // GSI 인증 상태가 끊긴다. 따라서 중앙 본문은 셸이면 항상 렌더하고, 각 portal 은
  // 슬롯별로 독립 가드한다(ForumJcgIndex/HomeDashboard custom 셸 계약과 동일).
  if (!inShell) {
    return <>{fallback}</>;
  }

  return (
    <>
      {/* 가운데(중앙 컬럼) — 캐러셀 + 세일즈 본문. 셸이면 항상 렌더. */}
      {children}

      {/* 좌측 슬롯 — Complete 이용권 안내 + 적중 실적 등 컴팩트 사이드 배너(leftAside).
          좌측 사이드바를 접으면 슬롯이 null 이 되어 이 portal 만 생략된다(접힌 상태라 정상).
          중앙 본문·우측 CTA 는 영향 없음. leftAside 가 없는 상품(null)이면 portal 자체를
          생략해 빈 컬럼만 남긴다(좌측 슬롯 래퍼는 셸이 소유). */}
      {leftSlot && leftAside && createPortal(leftAside, leftSlot)}

      {/* 우측 슬롯 — sticky 구매 CTA(ProductInfo 결제 박스). 좌측 슬롯과 독립 가드하여
          좌측 접기/펼치기에도 결제 박스가 계속 마운트된 채 유지된다. aside 가 sticky
          h-screen 이라 스크롤 내내 따라온다(ShellFrame). 슬롯 자체가 overflow-y-auto 라
          긴 CTA 도 안전. ProductInfo 루트의 `lg:w-1/3`(캐러셀 옆 폭)을 슬롯에선 전체 폭으로
          덮어쓴다(`[&>div]:lg:!w-full` — 결제 로직은 ProductInfo 내부라 무영향, 폭만 조정). */}
      {rightSlot &&
        createPortal(
          <div className="pt-1 [&>div]:lg:!w-full [&>div]:!mt-0">{cta}</div>,
          rightSlot,
        )}
    </>
  );
}
